import React from "react";
import { motion } from "framer-motion";
import { BookOpen, Globe2, ShieldCheck, Smartphone, Sparkles, Target, Workflow } from "lucide-react";

const pillars = [
  {
    title: "Education First",
    text: "We help users understand before they act",
    icon: BookOpen,
    accent: "from-amber-500/20 to-transparent",
  },
  {
    title: "High-Intent Leads",
    text: "Users already know what they need",
    icon: Target,
    accent: "from-[#FFD221]/25 to-transparent",
  },
  {
    title: "Verified Providers",
    text: "Only vetted partners are listed",
    icon: ShieldCheck,
    accent: "from-emerald-500/15 to-transparent",
  },
  {
    title: "Structured Journey",
    text: "From question → understanding → action",
    icon: Workflow,
    accent: "from-[#FFD221]/20 to-transparent",
  },
];

const featured = {
  title: "Built for Africa",
  text: "Designed for mobile, SMS, and real user behavior",
  Icon: Smartphone,
};

/**
 * Differentiation section — cream / gold / black InfoBoxx theme.
 */
const WhyChooseInfoBoxx = () => {
  const { Icon: FeaturedIcon, title: featuredTitle, text: featuredText } = featured;
  return (
    <section className="relative overflow-hidden bg-[#f7f4ec] px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
    <div
      className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-[#FFD221]/15 blur-[100px]"
      aria-hidden
    />
    <div
      className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#FFD221]/10 blur-[90px]"
      aria-hidden
    />
    <div
      className="pointer-events-none absolute left-1/2 top-0 h-px w-[min(100%,56rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-black/10 to-transparent"
      aria-hidden
    />

    <div className="relative z-10 mx-auto max-w-7xl">
      <motion.div
        className="mx-auto mb-14 max-w-3xl text-center lg:mb-16"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/18 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-[#9a7b00] shadow-sm">
          <Sparkles size={14} strokeWidth={2.5} className="text-[#c9a000]" aria-hidden />
          Why InfoBoxx · Differentiation
        </span>
        <h2 className="text-[clamp(2rem,4.5vw+0.5rem,3.25rem)] font-black leading-[1.08] tracking-tight text-gray-950">
          Why Choose{" "}
          <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#e8cc4c] bg-clip-text text-transparent">
            InfoBoxx?
          </span>
        </h2>
      </motion.div>

      <div className="grid gap-4 sm:grid-cols-2 lg:gap-5">
        {pillars.map(({ title, text, icon: Icon, accent }, index) => (
          <motion.article
            key={title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-24px" }}
            transition={{ duration: 0.45, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="group relative overflow-hidden rounded-[1.65rem] border border-black/[0.07] bg-white p-6 shadow-[0_18px_50px_-42px_rgba(0,0,0,0.45)] sm:p-7"
          >
            <div
              className={`pointer-events-none absolute -right-8 -top-8 h-36 w-36 rounded-full bg-gradient-to-br ${accent} opacity-80 blur-2xl transition-opacity group-hover:opacity-100`}
              aria-hidden
            />
            <div className="relative flex gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#FFD221] text-black shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] ring-2 ring-black/5">
                <Icon size={26} strokeWidth={2.25} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-black text-gray-950 sm:text-xl">{title}</h3>
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFD221]/25 text-[11px]"
                    aria-hidden
                  >
                    ✔️
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-[0.9375rem]">{text}</p>
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-24px" }}
        transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -3 }}
        className="relative mt-4 overflow-hidden rounded-[1.65rem] border-2 border-[#FFD221]/45 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-6 shadow-[0_28px_80px_-40px_rgba(0,0,0,0.55)] sm:mt-5 sm:p-8 lg:mt-6"
      >
        <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-[#FFD221] via-[#ffec80] to-[#c9a000]" aria-hidden />
        <div
          className="pointer-events-none absolute -right-20 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-[#FFD221]/12 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between sm:gap-10">
          <div className="flex flex-1 items-start gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#FFD221] text-black shadow-lg ring-2 ring-white/20">
              <FeaturedIcon size={30} strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xl" aria-hidden>
                  ✔️
                </span>
                <h3 className="text-xl font-black text-white sm:text-2xl">{featuredTitle}</h3>
              </div>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-base">{featuredText}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 text-[#FFD221] sm:flex-col sm:items-end">
            <Globe2 size={36} strokeWidth={1.75} className="opacity-90 sm:h-10 sm:w-10" aria-hidden />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FFD221]/80">
              Local context
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

export default WhyChooseInfoBoxx;
